/**
 * Ziyaret Hareket Tipleri — sözlük listesi (ZIYARET_TAKIP.md §2.5).
 *
 * Bu sözlüğün varlık sebebi yeni bir hareket tipi eklemenin DEPLOY GEREKTİRMEMESİ:
 * akıştaki etiket/ikon/renk, GPS gibi gürültülü tiplerin gizlenmesi ve "bu hareket
 * ziyareti başarılı yapar mı" (§4.2) kararı buradan gelir.
 *
 * c360 liste altyapısı üzerine kuruludur (repo standardı).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import { useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon, DeleteIcon } from './c360';
import { usePageActions } from '../contexts/PermissionContext';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/visitactivitytypes';
const LIST_ROUTE = '/visit-activity-types';

const GROUP_LABELS = {
  visit: 'Ziyaret',
  document: 'Belge',
  collection: 'Tahsilat',
  survey: 'Anket',
  gps: 'GPS',
  stock: 'Stok',
  day: 'Gün',
  other: 'Diğer',
};

const COLUMN_FILTER_META = {
  code: { type: 'text', odataField: 'Code' },
  nameTr: { type: 'text', odataField: 'NameTr' },
  groupCode: { type: 'text', odataField: 'GroupCode' },
};

export function VisitActivityTypesPage() {
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  usePageMeta('Ziyaret Hareket Tipleri', 'Saha İzleme akışındaki hareket sözlüğü.');

  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const searchFields = useMemo(() => ['Code', 'NameTr'], []);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields,
    defaultSortField: 'SortOrder',
    defaultSortDir: 'asc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openDetail = (row = selected, view = false) => {
    if (!row) return;
    navigate(`${LIST_ROUTE}/${row.id}${view ? '' : '?edit=1'}`, { state: { row, view } });
  };

  /** Silme = pasifleştirme (Code silinmez — geçmiş hareketler ona bağlı). */
  const handleDeactivate = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`"${row.nameTr}" hareket tipi pasifleştirilecek. Geçmiş kayıtlar korunur. Onaylıyor musunuz?`)) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: row.id }),
      });
      if (!res.ok) throw new Error('Hareket tipi pasifleştirilemedi.');
      showToast('success', 'Hareket tipi pasifleştirildi.');
      fetchData();
    } catch (e) {
      showToast('error', e.message);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('code', {
      header: 'Kod', meta: { filterType: 'text' },
      cell: (info) => <span className="c360-erp-chip">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('nameTr', { header: 'Ad', meta: { filterType: 'text' }, cell: (i) => i.getValue() || '-' }),
    columnHelper.accessor('groupCode', {
      header: 'Grup', meta: { filterType: 'text' },
      cell: (i) => GROUP_LABELS[i.getValue()] || i.getValue() || '-',
    }),
    columnHelper.accessor('verbTr', { header: 'Akış Cümlesi', enableColumnFilter: false, cell: (i) => i.getValue() || '-' }),
    columnHelper.accessor('isTimelineVisible', {
      header: 'Akışta Görünür', enableColumnFilter: false,
      cell: (i) => (i.getValue() ? 'Evet' : 'Hayır'),
    }),
    columnHelper.accessor('isSuccessSignal', {
      header: 'Başarı Sinyali', enableColumnFilter: false,
      cell: (i) => (i.getValue() ? <span className="c360-erp-chip">Evet</span> : 'Hayır'),
    }),
    columnHelper.accessor('requiresReason', {
      header: 'Neden Zorunlu', enableColumnFilter: false,
      cell: (i) => (i.getValue() ? 'Evet' : 'Hayır'),
    }),
    columnHelper.accessor('sortOrder', { header: 'Sıra', enableColumnFilter: false, cell: (i) => i.getValue() ?? '-' }),
    columnHelper.accessor('isActive', {
      header: 'Durum', enableColumnFilter: false,
      cell: (i) => (i.getValue() ? 'Aktif' : 'Pasif'),
    }),
    columnHelper.display({
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" style={s.iconBtn} title="İzle"
            onClick={(e) => { e.stopPropagation(); openDetail(row.original, true); }}>
            <ViewIcon />
          </button>
          {canEdit && (
            <button type="button" style={s.iconBtn} title="Düzenle"
              onClick={(e) => { e.stopPropagation(); openDetail(row.original, false); }}>
              <EditIcon />
            </button>
          )}
          {canDelete && row.original.isActive && (
            <button type="button" style={s.iconBtn} title="Pasifleştir"
              onClick={(e) => { e.stopPropagation(); handleDeactivate(row.original); }}>
              <DeleteIcon />
            </button>
          )}
        </div>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <ListPageShell
      title="Ziyaret Hareket Tipleri"
      subtitle="Saha İzleme akışında görünen hareketlerin sözlüğü. Kod sabittir, silinmez."
      search={{ value: filterInput, onChange: setFilterInput, onSubmit: applyFilterText, placeholder: 'Kod veya ad ara…' }}
      actions={(
        <>
          <ColumnChooser table={table} />
          <button type="button" style={s.secondaryBtn}
            onClick={() => exportRows().then((data) => exportToExcel(data, 'ziyaret-hareket-tipleri'))}>
            <ExcelIcon />Excel
          </button>
          {canAdd && (
            <button type="button" style={newRecordButtonStyle} onClick={() => navigate(`${LIST_ROUTE}/new`)}>
              Yeni Hareket Tipi
            </button>
          )}
        </>
      )}
    >
      <C360Table
        table={table}
        loading={loading}
        rows={rows}
        selectedId={selected?.id}
        onRowClick={(row) => setSelected(row)}
        onRowDoubleClick={(row) => openDetail(row, true)}
        sortField={toGridSortField(sortField)}
        sortDir={sortDir}
        onSort={(field) => applySort(toODataSortField(field))}
        columnFilters={columnFilters}
        onColumnFilter={setColumnFilter}
        onClearColumnFilter={clearColumnFilter}
        emptyText={filterText ? 'Aramaya uygun hareket tipi yok.' : 'Hareket tipi tanımı yok.'}
      />
      <Pagination
        totalCount={totalCount}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
      <Toast toast={toast} />
    </ListPageShell>
  );
}
